import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type { BenchmarkInsightCompetitorsDesiredCompetitor } from 'lumen/models';

import styles from './desired-compset-status.module.css';

interface HotelBenchmarkInsightDesiredCompsetStatusSignature {
  Args: {
    desiredCompetitors: BenchmarkInsightCompetitorsDesiredCompetitor[];
  };
}

export default class HotelBenchmarkInsightDesiredCompsetStatus extends Component<HotelBenchmarkInsightDesiredCompsetStatusSignature> {
  @service declare access: Services['access'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get amountOfAvailableCompetitors() {
    return this.args.desiredCompetitors.filter(
      (competitor) => competitor.compsetReady,
    ).length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get desiredCompsetSize() {
    return this.args.desiredCompetitors.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isClickable() {
    return this.access.hasBenchmarkInsightWriterRole;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get progressCircleBackgroundGradient() {
    const progressPercentageDegrees = (this.progressPercentage / 100) * 360;

    return htmlSafe(
      `background: conic-gradient(
      var(--prism-color-icon-success-default) 0deg ${progressPercentageDegrees}deg,
      var(--prism-color-border-neutral-default) ${progressPercentageDegrees}deg 360deg
    )`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get progressPercentage() {
    if (this.desiredCompsetSize === 0) {
      return 0;
    }
    return Math.round(
      (this.amountOfAvailableCompetitors / this.desiredCompsetSize) * 100,
    );
  }

  @action
  onClick(): void {
    if (!this.isClickable) {
      return;
    }

    // Use legacyTransitionTo to avoid the benchmark-insight model refreshing
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.benchmark-insight.desired-compset');

    this.usageAnalytics.track('Compset Wishlist Editing', {
      eventTrigger: 'desired-compset-status',
    });
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div
      class="flex flex-row items-center gap-200
        {{if this.isClickable 'cursor-pointer hover:underline'}}"
      data-test-id="desired-compset-status"
      role="button"
      {{on "click" this.onClick}}
    >
      {{#if (eq this.amountOfAvailableCompetitors this.desiredCompsetSize)}}
        <PrismIcon
          @color="success"
          @name="circle-checkmark"
          data-test-icon="circle-checkmark"
        />
      {{else}}
        <div
          class={{styles.progress-circle}}
          data-test-icon="progress-circle"
          style={{this.progressCircleBackgroundGradient}}
        >
          <div class={{styles.inner-circle}}></div>
        </div>
      {{/if}}

      <div class="text-300--regular text-nowrap" data-test-status>
        {{t
          "benchmark_insight.desired_compset.desired_competitors"
          amountOfAvailableCompetitors=this.amountOfAvailableCompetitors
          desiredCompsetSize=this.desiredCompsetSize
        }}
      </div>
    </div>
  </template>
}
